import React, { useEffect, useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import {
  FeedListItem,
  FeedListInfinitySpinner,
  LoaderDashboard,
  DashboardHeading,
  Button,
} from 'components';
import { HomeFeedNoResults, HomeActivityFeedPolling } from 'screens/HomeScreen';
import { HomeActivityFeedQuery, handleFetchMoreError, criticalFeedError } from 'services/apollo';
import { useInView } from 'react-intersection-observer';
import styles from './HomeActivityFeed.module.scss';
import { trackingActivityFeed } from 'services/analytics';
import { useFeatureFlagState } from 'context';
import { useQueryLifecycle } from 'hooks/useQueryLifecycle';
import Icon from 'assets/icons';
import { Logger, getFlag, flags } from 'services';
import produce from 'immer';

export const HomeActivityFeed = () => {
  const client = useApolloClient();
  const { groupsFeatureFlag } = useFeatureFlagState();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  // Polling
  const [startPolling, setStartPolling] = useState(false);
  // Subscriptions
  // const [startedFetch, setStartedFetch] = useState(false);
  // const [isCached, setIsCached] = useState(true);
  const [pollingResult, setPollingResult] = useState<any>(false);

  const { error, data, fetchMore, networkStatus } = useQuery(HomeActivityFeedQuery, {
    variables: { first: 20, cursor: '', includeGroups: groupsFeatureFlag ? true : false },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const { isCached: showingCachedData } = useQueryLifecycle(networkStatus);

  const [ref, inView] = useInView({
    rootMargin: '500px 0px',
    threshold: 0,
  });

  /**
   * Start polling
   */
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      if (getFlag(flags.PollingEnabled)) {
        setStartPolling(true);
      }
    }, 300000);
    return () => {
      // Return callback to run on unmount.
      window.clearTimeout(timer);
    };
  }, []);

  /**
   * Replaces the data query payload
   * with the newData so we don't have to fetch again
   */
  const replaceData = React.useCallback(() => {
    const readQueryResult: any = client.readQuery({
      query: HomeActivityFeedQuery,
      variables: {
        first: 20,
        cursor: '',
        includeGroups: groupsFeatureFlag ? true : false,
      },
    });
    const oldFeed = readQueryResult.me.activityFeed;
    if (pollingResult) {
      const newFeed = produce(oldFeed, (draft: any) => {
        draft.nodes = pollingResult.me.activityFeed.nodes;
        draft.pageInfo = pollingResult.me.activityFeed.pageInfo;
      });
      try {
        client.writeQuery({
          query: HomeActivityFeedQuery,
          variables: {
            first: 20,
            cursor: '',
            includeGroups: groupsFeatureFlag ? true : false,
          },
          data: {
            me: {
              activityFeed: newFeed,
              __typename: 'Me',
            },
          },
        });
      } catch (err) {
        Logger.log(err);
      }
    }
  }, [client, pollingResult, groupsFeatureFlag]);

  /**
   * Scrolls the list to the top on mount
   */
  useEffect(() => {
    window.scroll({ top: 0 });
  }, []);

  /**
   * Checks if the first item in the polling result is new
   * been updated and, if so, reacts accordingly
   */
  useEffect(() => {
    if (!data || !pollingResult) {
      return;
    }

    const didFirstItemChange =
      pollingResult.me.activityFeed.nodes[0]?.id !== data?.me.activityFeed.nodes[0]?.id;

    if (didFirstItemChange) {
      if (window.scrollY > 150) {
        setShowScrollToTop(true);
      } else {
        replaceData();
      }
    }
  }, [replaceData, pollingResult, data]);

  /**
   * Analytics tracking
   */
  useEffect(() => {
    if (!showingCachedData) {
      const totalNodes = data?.me?.activityFeed?.pageInfo?.total || 0;
      trackingActivityFeed(totalNodes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingCachedData]);

  /**
   * Infinite scroll
   * Fetches more results
   */
  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.me.activityFeed.pageInfo;

    try {
      await fetchMore({
        variables: { first: 20, cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.me.activityFeed.nodes = [
            ...prevResult.me.activityFeed.nodes,
            ...fetchMoreResult.me.activityFeed.nodes,
          ];
          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined) return <LoaderDashboard layout="Feed" />;

  if (error && criticalFeedError(error)) {
    return <LoaderDashboard layout="Feed" />;
  }

  if (!data.me.activityFeed.nodes.length) return <HomeFeedNoResults />;

  /**
   * Scrolls the list to top and
   * refetches the first 20 results
   */
  const gotoTopAndRefresh = () => {
    setIsLoadingMore(false);
    setShowScrollToTop(false);
    window.scroll({ top: 0, behavior: 'smooth' });
    window.setTimeout(() => replaceData(), 1000);
  };

  return (
    <>
      <div className={styles.Heading}>
        <DashboardHeading level={1}>Activity Feed</DashboardHeading>
      </div>
      {data.me.activityFeed.nodes.map((node: any, key: number) => (
        <FeedListItem
          nodeId={node.id}
          nodeType={node.__typename}
          key={key}
          name={node.title}
          baseUrl={node.baseUrl}
          url={node.url}
          owner={node.owners[0]}
          collaborators={node.accessorInfo}
          modified={node.modified}
          created={node.created}
          nodeIcon={node.category}
          isIntegrationConnected={node.isIntegrationConnected}
          integrationType={node.integrationType}
          trackIndex={key}
          trackFeedType={'HomeActivityFeed'}
          trackTotalItems={data.me.activityFeed.pageInfo.total}
          isShareable={node.sharingEnabled}
          shareTargets={node.shareTargets}
          isCached={showingCachedData}
        />
      ))}

      {showScrollToTop && (
        <Button className={styles.scrollToTopButton} size="Medium" onClick={gotoTopAndRefresh}>
          <Icon.ArrowUp /> <p>See what's new!</p>
        </Button>
      )}

      {data.me.activityFeed.pageInfo.hasNextPage && (
        <div ref={ref}>
          <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
        </div>
      )}
      {startPolling && <HomeActivityFeedPolling setPollingResult={setPollingResult} />}
    </>
  );
};
